import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { isApprover, useAuth, usesAdminShell } from '../auth/AuthContext'
import { acknowledgeNotification, listNotifications, listChaseList, listPendingQualifications } from '../lib/api'
import { detectPlatform, isStandalone, notificationPermission } from '../lib/platform'
import { registerServiceWorker } from '../lib/push'
import { useLiveReload } from '../lib/useLiveReload'
import { DemoResetButton } from './DemoResetButton'
import { DemoRibbon } from './ui'
import type { AppRole } from '../types/db'
import {
  BellIcon,
  BookIcon,
  CalendarIcon,
  CheckBadgeIcon,
  HomeIcon,
  LogoutIcon,
  ShieldIcon,
  WalletIcon,
} from './Icons'

/* -------------------------------------------------------------------------- */
/* Notification plumbing                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A tapped push notification lands here. The service worker appends ?n=<id> to
 * the target URL, and either focuses an existing window with a postMessage or
 * opens a new one. Both paths end with the reminder being acknowledged, which
 * is what takes the coach off Michel's chase list.
 */
function useNotificationAcknowledgement(onChanged: () => void) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const id = params.get('n')
    if (!id) return
    void acknowledgeNotification(id)
      .catch(() => undefined)
      .then(() => {
        const next = new URLSearchParams(params)
        next.delete('n')
        setParams(next, { replace: true })
        onChanged()
      })
  }, [params, setParams, onChanged])

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    function onMessage(event: MessageEvent) {
      const data = event.data as { type?: string; url?: string } | null
      if (data?.type === 'NOTIFICATION_OPENED' && data.url) {
        navigate(data.url)
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])
}

/* -------------------------------------------------------------------------- */
/* Shell                                                                      */
/* -------------------------------------------------------------------------- */

export function AppShell() {
  const { profile, role, demoMode, signOut } = useAuth()
  const location = useLocation()
  const admin = usesAdminShell(role)
  const [unread, setUnread] = useState(0)
  const [counts, setCounts] = useState({ approvals: 0, chase: 0 })

  const approver = isApprover(profile)

  /**
   * Only what this person's nav shows. On the live backend RLS would refuse
   * the approvals query for Gina and the chase view for a coach, so asking
   * would be noise; in the demo it is simply unnecessary.
   */
  const refreshBadges = useCallback(() => {
    if (!profile) return
    const jobs: Array<Promise<unknown>> = []
    if (approver) {
      jobs.push(
        listPendingQualifications().then((rows) =>
          setCounts((c) => ({ ...c, approvals: rows.length })),
        ),
      )
    }
    if (role === 'admin') {
      jobs.push(
        listChaseList().then((rows) => setCounts((c) => ({ ...c, chase: rows.length }))),
      )
    }
    if (!admin) {
      jobs.push(
        listNotifications(profile.id).then((rows) =>
          setUnread(rows.filter((r) => !r.acknowledged_at).length),
        ),
      )
    }
    void Promise.all(jobs).catch(() => undefined)
  }, [profile, admin, approver, role])

  useEffect(() => {
    refreshBadges()
  }, [refreshBadges, location.pathname])

  // A write anywhere in the demo (an approval, an acknowledged reminder) moves
  // the badge counts without anyone navigating.
  useLiveReload(refreshBadges)

  useEffect(() => {
    void registerServiceWorker()
  }, [])

  useNotificationAcknowledgement(refreshBadges)

  const setupNeeded = useMemo(() => {
    if (admin) return false
    if (typeof window === 'undefined') return false
    return !isStandalone() || notificationPermission() !== 'granted'
  }, [admin])

  if (!profile) return null

  return (
    <div className={`shell ${admin ? 'shell--admin' : 'shell--coach'}`}>
      {demoMode ? <DemoRibbon /> : null}

      <header className="topbar">
        <div className="topbar__brand">
          <span className="topbar__mark">LF</span>
          <span>
            La Folie
            <span className="topbar__sub">{admin ? 'Operations' : 'Coach portal'}</span>
          </span>
        </div>

        {!admin && approver ? (
          <NavLink to="/admin/approvals" className="topbar__action" aria-label="Approvals">
            <CheckBadgeIcon />
            {counts.approvals > 0 ? (
              <span className="topbar__badge">{counts.approvals}</span>
            ) : null}
          </NavLink>
        ) : null}

        {!admin ? (
          <NavLink to="/inbox" className="topbar__action" aria-label="Notifications">
            <BellIcon />
            {unread > 0 ? <span className="topbar__badge">{unread}</span> : null}
          </NavLink>
        ) : null}

        <button
          type="button"
          className="topbar__action"
          onClick={() => void signOut()}
          aria-label="Sign out"
          title={`Signed in as ${profile.full_name}`}
        >
          <LogoutIcon />
        </button>
      </header>

      <div className="shell__body">
        {admin ? <AdminNav role={role} counts={counts} /> : null}
        <main className="main">
          {setupNeeded && location.pathname !== '/setup' ? <SetupBanner /> : null}
          <Outlet />
          {admin && demoMode ? (
            <div
              className="row"
              style={{ justifyContent: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-6)' }}
            >
              <span className="faint">Demo data.</span>
              <DemoResetButton label="Start the demo again" />
            </div>
          ) : null}
        </main>
      </div>

      {admin ? null : <CoachTabs unread={unread} />}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Navigation                                                                 */
/* -------------------------------------------------------------------------- */

const COACH_TABS = [
  { to: '/', label: 'Home', Icon: HomeIcon, end: true },
  { to: '/events', label: 'Events', Icon: CalendarIcon, end: false },
  { to: '/training', label: 'Training', Icon: BookIcon, end: false },
  { to: '/earnings', label: 'Earnings', Icon: WalletIcon, end: false },
  { to: '/record', label: 'Record', Icon: ShieldIcon, end: false },
]

function CoachTabs({ unread }: { unread: number }) {
  return (
    <nav className="tabbar" aria-label="Main">
      {COACH_TABS.map(({ to, label, Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }) => `tab${isActive ? ' is-active' : ''}`}
        >
          <Icon />
          {label}
          {to === '/' && unread > 0 ? <span className="tab__dot" /> : null}
        </NavLink>
      ))}
    </nav>
  )
}

type BadgeKey = 'approvals' | 'chase' | null

interface AdminLink {
  to: string
  label: string
  end: boolean
  key: BadgeKey
}

const EVENTS_LINK: AdminLink = { to: '/admin/events', label: 'Events', end: false, key: null }
const ROSTER_LINK: AdminLink = { to: '/admin/roster', label: 'Roster', end: false, key: null }

/** Each core-team role sees the screens built for its job, home first. */
function adminLinks(role: AppRole | null): AdminLink[] {
  if (role === 'materials') {
    return [{ to: '/materials', label: 'Materials', end: true, key: null }, EVENTS_LINK, ROSTER_LINK]
  }
  if (role === 'finance') {
    return [{ to: '/finance', label: 'Finance', end: true, key: null }, EVENTS_LINK, ROSTER_LINK]
  }
  return [
    { to: '/admin', label: 'Dashboard', end: true, key: null },
    EVENTS_LINK,
    ROSTER_LINK,
    { to: '/admin/approvals', label: 'Approvals', end: false, key: 'approvals' },
    { to: '/admin/chase', label: 'Chase list', end: false, key: 'chase' },
  ]
}

function AdminNav({
  role,
  counts,
}: {
  role: AppRole | null
  counts: { approvals: number; chase: number }
}) {
  return (
    <nav className="adminnav" aria-label="Main">
      {adminLinks(role).map(({ to, label, end, key }) => {
        const count = key ? counts[key] : 0
        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `adminnav__link${isActive ? ' is-active' : ''}`}
          >
            {label}
            {count > 0 ? <span className="adminnav__count">{count}</span> : null}
          </NavLink>
        )
      })}
    </nav>
  )
}

/* -------------------------------------------------------------------------- */
/* Setup banner                                                               */
/* -------------------------------------------------------------------------- */

function SetupBanner() {
  const platform = detectPlatform()
  const installed = isStandalone()
  const permission = notificationPermission()

  const message = !installed
    ? platform === 'ios'
      ? 'Add La Folie to your home screen so reminders can reach you. On iPhone this is required.'
      : 'Install La Folie on your phone so reminders reach you reliably.'
    : permission === 'denied'
      ? 'Notifications are blocked, so you will miss reminders. Here is how to turn them back on.'
      : 'Turn on notifications so you get your event reminders 48 and 24 hours ahead.'

  return (
    <NavLink to="/setup" className="card card--attention" style={{ marginBottom: 'var(--sp-4)' }}>
      <div className="row row--between">
        <div>
          <p className="card__title">Finish setting up</p>
          <p className="small muted" style={{ marginTop: 2 }}>
            {message}
          </p>
        </div>
        <span className="badge badge--warning">1 min</span>
      </div>
    </NavLink>
  )
}
